// sim/slotBudget.ts — SLOT POWER BUDGETS: every caste slot has an expected share of the
// caste's power, and NO single gene may dominate the loadout.
//
// THE GOAL (from the brief): progression should come from building a COMPLETE loadout, not
// from chasing one jackpot drop. So a caste's power is budgeted across its slots — each
// slot targets a bounded, predictable share — and a single gene's contribution is held to
// a small band (≈5–15% of caste power), never an overwhelming 50–80%.
//
// HOW IT RELATES to the rest of the power pipeline (this module is the explicit MODEL +
// the guard; the enforcement already lives in two sibling layers):
//   • genePowerBudget.ts — bounds each APPLY's gain to a rarity band % of current caste
//     power (so one drop adds a bounded slice; see the cross-module test).
//   • geneLootScaling.ts — generates each REWARD in a window around its slot's baseline
//     (so loot is an increment over what that slot holds, never a caste-sized jackpot).
//   • THIS module — names the per-slot targets + the per-gene contribution band, computes a
//     gene's share, classifies it, and offers `capPowerForShare` as the hard ceiling. Used
//     to make the budget VISIBLE (the inventory / box-reveal forecast) and as a reusable
//     guard.
//
// PURE: integer-friendly, no RNG, no sim mutation, never hashed/saved. Directly testable.

// ---------------------------------------------------------------------------
// Contribution band — a single gene's share of its caste's TOTAL power, as PERCENT.
// ---------------------------------------------------------------------------

/** A well-built loadout has each gene contributing in this band — many modest genes. */
export const TARGET_SHARE_LO_PCT = 5;
export const TARGET_SHARE_HI_PCT = 15;
/** Hard ceiling: a single gene should NEVER exceed this share of caste power (the brief's
 *  "never 50–80%"). Drives the dominance flag + `capPowerForShare`. */
export const MAX_SHARE_PCT = 25;

/** Where a gene's contribution sits relative to the band. */
export type ShareClass = 'minor' | 'on-target' | 'heavy' | 'dominant';

/**
 * A gene's contribution as an integer percent of the caste's total power. 0 when either
 * input is non-positive (an empty caste has no shares). Pure.
 */
export function contributionSharePct(genePower: number, casteTotalPower: number): number {
  if (casteTotalPower <= 0 || genePower <= 0) return 0;
  return Math.round((100 * genePower) / casteTotalPower);
}

/**
 * Classify a share percent: below the band is `minor`, inside it is `on-target`, above the
 * band but within the ceiling is `heavy`, and past the ceiling is `dominant` (the gene the
 * "no jackpot" rule warns about). Pure.
 */
export function classifyShare(pct: number): ShareClass {
  if (pct > MAX_SHARE_PCT) return 'dominant';
  if (pct > TARGET_SHARE_HI_PCT) return 'heavy';
  if (pct < TARGET_SHARE_LO_PCT) return 'minor';
  return 'on-target';
}

// ---------------------------------------------------------------------------
// Per-slot target curve — "every caste slot has an expected power range".
// ---------------------------------------------------------------------------

/** First→last slot growth: each later slot targets `SLOT_GROWTH` more than the previous
 *  (linear). 0.5 reproduces the brief's 100 / 150 / 200 over three slots. */
export const SLOT_GROWTH = 0.5;

/**
 * The expected power for one (0-based) slot of a caste whose total power budget is
 * `casteBudget`, spread over `slotCount` slots that grow gently from first to last. The
 * per-slot targets SUM to ~`casteBudget`, so each slot is a bounded, predictable share
 * (no slot is meant to hold the whole caste). With `casteBudget=450, slotCount=3` this is
 * 100 / 150 / 200 — the brief's example. Pure; clamps the index into range.
 */
export function slotTargetPower(slotIndex: number, casteBudget: number, slotCount: number): number {
  if (slotCount <= 0 || casteBudget <= 0) return 0;
  let weightSum = 0;
  for (let i = 0; i < slotCount; i++) weightSum += 1 + SLOT_GROWTH * i;
  const idx = slotIndex < 0 ? 0 : slotIndex >= slotCount ? slotCount - 1 : slotIndex;
  const w = 1 + SLOT_GROWTH * idx;
  return Math.round((casteBudget * w) / weightSum);
}

/**
 * The maximum power a single gene may have for its share of the caste to stay within
 * `maxSharePct`, given the power of the REST of the caste (everything but this gene):
 *   share = g / (rest + g) ≤ m   ⇒   g ≤ rest · m / (1 − m).
 * A reusable hard cap for a generated/applied gene. Pure integer floor. `rest ≤ 0` ⇒ 0
 * (the first gene in an empty caste has no "rest" to measure against).
 */
export function capPowerForShare(restOfCastePower: number, maxSharePct: number = MAX_SHARE_PCT): number {
  const m = Math.max(0, Math.min(99, maxSharePct)) / 100;
  if (m >= 1) return Number.MAX_SAFE_INTEGER;
  if (restOfCastePower <= 0) return 0;
  return Math.floor((restOfCastePower * m) / (1 - m));
}
